/**
 * Shared constants and helper functions for chart filter configuration.
 * Used by both FilterConfigurator, EditChartModal, and ChartPreview.
 */

export const FILTER_TYPES = [
  { id: 'select', name: 'Single Select', icon: '📋' },
  { id: 'multiselect', name: 'Multi Select', icon: '☑️' },
  { id: 'daterange', name: 'Date Range', icon: '📅' },
  { id: 'text', name: 'Text Search', icon: '🔍' },
  { id: 'number', name: 'Number', icon: '🔢' }
];

export const DATE_COLUMN_TYPES = [
  { id: 'STRING', name: 'String (ISO format)' },
  { id: 'TIMESTAMP', name: 'Timestamp' },
  { id: 'DATETIME', name: 'DateTime' },
  { id: 'DATE', name: 'Date' },
  { id: 'EXCEL_SERIAL', name: 'Excel Serial Number (FLOAT64)' }
];

export const DATE_RANGE_PRESETS = [
  { id: 'today', name: 'Today' },
  { id: 'yesterday', name: 'Yesterday' },
  { id: 'last7days', name: 'Last 7 Days' },
  { id: 'last30days', name: 'Last 30 Days' },
  { id: 'thisMonth', name: 'This Month' },
  { id: 'lastMonth', name: 'Last Month' },
  { id: 'thisYear', name: 'This Year' }
];

/**
 * Extract columns/aliases from a SQL SELECT query.
 * Handles: column, column AS alias, table.column, aggregate(column) AS alias, etc.
 */
export function extractColumnsFromSQL(sql) {
  if (!sql || typeof sql !== 'string') return [];

  try {
    const normalizedSQL = sql.replace(/\s+/g, ' ').trim();
    const selectMatch = normalizedSQL.match(/SELECT\s+([\s\S]*?)\s+FROM/i);
    if (!selectMatch) return [];

    const selectClause = selectMatch[1];
    if (selectClause.trim() === '*') return [];

    const columns = [];
    let current = '';
    let parenDepth = 0;

    for (let i = 0; i < selectClause.length; i++) {
      const char = selectClause[i];
      if (char === '(') parenDepth++;
      else if (char === ')') parenDepth--;
      else if (char === ',' && parenDepth === 0) {
        if (current.trim()) columns.push(current.trim());
        current = '';
        continue;
      }
      current += char;
    }
    if (current.trim()) columns.push(current.trim());

    return columns.map(col => {
      col = col.trim();

      const asMatch = col.match(/\s+AS\s+[`']?(\w+)[`']?\s*$/i);
      if (asMatch) {
        return {
          name: asMatch[1],
          expression: col.replace(/\s+AS\s+[`']?\w+[`']?\s*$/i, '').trim(),
          isAlias: true
        };
      }

      const implicitMatch = col.match(/\)\s+(\w+)\s*$/);
      if (implicitMatch) {
        return {
          name: implicitMatch[1],
          expression: col.replace(/\s+\w+\s*$/, '').trim(),
          isAlias: true
        };
      }

      const simpleMatch = col.match(/(?:[\w`']+\.)?[`']?(\w+)[`']?\s*$/);
      if (simpleMatch) {
        return {
          name: simpleMatch[1],
          expression: col,
          isAlias: false
        };
      }

      return {
        name: col.replace(/[^a-zA-Z0-9_]/g, '_'),
        expression: col,
        isAlias: false
      };
    }).filter(c => c.name && !c.name.match(/^\d+$/));
  } catch (e) {
    console.error('Error parsing SQL:', e);
    return [];
  }
}

/**
 * Infer the likely filter type and column type based on column name and schema type.
 */
export function inferColumnType(columnName, schemaType) {
  const name = columnName.toLowerCase();
  const type = (schemaType || '').toUpperCase();

  if (name.includes('date') || name.includes('time') || name.includes('created') ||
      name.includes('updated') || name.includes('timestamp') || name.endsWith('_at') ||
      name.includes('day') || name.includes('month') || name.includes('year')) {
    if (type === 'FLOAT64' || type === 'FLOAT' || type === 'INTEGER' || type === 'INT64') {
      return { filterType: 'daterange', columnType: 'EXCEL_SERIAL' };
    }
    if (type === 'TIMESTAMP') return { filterType: 'daterange', columnType: 'TIMESTAMP' };
    if (type === 'DATETIME') return { filterType: 'daterange', columnType: 'DATETIME' };
    if (type === 'DATE') return { filterType: 'daterange', columnType: 'DATE' };
    return { filterType: 'daterange', columnType: 'STRING' };
  }

  if (name.includes('count') || name.includes('amount') || name.includes('total') ||
      name.includes('sum') || name.includes('avg') || name.includes('price') ||
      name.includes('quantity') || name.includes('number') || name.includes('num_') ||
      name.startsWith('n_') || name.endsWith('_id')) {
    return { filterType: 'number', columnType: 'NUMBER' };
  }

  return { filterType: 'select', columnType: 'STRING' };
}

/**
 * Find the correct sourceTable for CTE-based queries.
 * Returns the CTE name if the column lives inside a CTE rather than the final SELECT.
 */
export function findSourceTableForColumn(columnName, queryStr) {
  if (!queryStr) return null;

  const ctePattern = /WITH\s+(\w+)\s+AS\s*\(/gi;
  const ctes = [];
  let match;
  while ((match = ctePattern.exec(queryStr)) !== null) {
    ctes.push(match[1]);
  }

  if (ctes.length === 0) return null;

  const finalSelectMatch = queryStr.match(/\)\s*SELECT\s+[\s\S]*$/i);
  if (!finalSelectMatch) return null;

  const finalSelect = finalSelectMatch[0];
  const columnRegex = new RegExp(`\\b${columnName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');

  if (!columnRegex.test(finalSelect)) {
    for (const cteName of ctes) {
      const cteContentRegex = new RegExp(`${cteName}\\s+AS\\s*\\(([^)]+(?:\\([^)]*\\)[^)]*)*?)\\)`, 'i');
      const cteMatch = queryStr.match(cteContentRegex);
      if (cteMatch && columnRegex.test(cteMatch[1])) {
        return cteName;
      }
    }
    if (ctes.length > 0) {
      return ctes[0];
    }
  }

  return null;
}
